import { ConceptRelation } from 'api/grpc/resources';
import { getConceptTextFromUiLabelItem } from 'components/CfUiItem/ConceptName';
import { hasConcepts, hasRegionsWithConcepts } from 'components/ModelOutputRenderer/components/utils';
import { updateAnnotationProperties } from './general';

// Extended version of `getConceptTextFromUiLabelItem` that also takes into account concept relations.
export const getConceptName = (concept: CF.API.ConceptPartial, conceptRelationMap: Map<string, CF.API.Concepts.ConceptRelation[]>) => {
  const relations = conceptRelationMap.get(concept.id);

  if (relations) {
    const lastRelation = relations[relations.length - 1];
    const { objectConcept } = lastRelation;

    return objectConcept.name;
  }

  return getConceptTextFromUiLabelItem(concept);
};

/**
 * Allows you to easily update concepts without having to deal with the complex annotation data structure.
 */
export const updateConceptsFromAnnotationData = (
  annotationData: CF.API.TOutputData,
  callback: (conceptsToFilter: CF.API.Concept[]) => CF.API.Concept[],
) => {
  if ('concepts' in annotationData && annotationData.concepts?.length) {
    return {
      ...annotationData,
      concepts: callback(annotationData.concepts),
    };
  } else if ('regions' in annotationData && annotationData.regions?.length) {
    const regions = annotationData.regions.reduce<CF.API.RegionsEntity[]>((filteredRegions, region) => {
      if (region.data.concepts?.length) {
        filteredRegions.push({
          ...region,
          data: {
            ...region.data,
            concepts: callback(region.data.concepts),
          },
        });
      }

      return filteredRegions;
    }, []);

    return {
      ...annotationData,
      regions,
    };
  } else {
    return annotationData;
  }
};

// Allow only current app concept relations
export const getHumanConceptRelations = (conceptRelations: ConceptRelation[], currentAppId: string): ConceptRelation[] => {
  return conceptRelations.filter((c) => {
    // If relations has same object/subject app, then it is human relation
    const objectHasCurrentApp = c.getObjectConcept()?.getAppId() === currentAppId;
    const subjectHasCurrentApp = c.getSubjectConcept()?.getAppId() === currentAppId;

    // If relations are equal they do not need to be passed to the store
    const namesAreEqual = c.getObjectConcept()?.getName() === c.getSubjectConcept()?.getName();
    const idsAreEqual = c.getObjectConcept()?.getId() === c.getSubjectConcept()?.getId();
    const areEqual = namesAreEqual && idsAreEqual;

    return !areEqual && objectHasCurrentApp && subjectHasCurrentApp;
  });
};

export const updateAnnotationConcepts = (annotation: CF.API.Annotations.Annotation, concepts: CF.API.Concept[]) => {
  if (hasConcepts(annotation.data) && annotation.data.concepts?.length) {
    return updateAnnotationProperties(annotation, {
      'data.concepts': concepts,
    });
  }

  if (hasRegionsWithConcepts(annotation.data) && annotation.data.regions?.length) {
    return updateAnnotationProperties(annotation, {
      'data.regions': annotation.data.regions.map((region) => ({
        ...region,
        data: {
          ...region.data,
          concepts,
        },
      })),
    });
  }

  return null;
};
